import { ThemeProvider } from '@mui/material';
import * as Sentry from '@sentry/react';
import { DYNAMIC_INVOICE_ID_TYPES } from '@vy/api-definition';
import { PERMISSIONS } from '@vy/types';
import { createPortal } from 'react-dom';
import { Navigate, Outlet, Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import { oldVyTheme } from './assets/old-themes/old-vy-theme';
import { vyTheme } from './assets/themes/vyTheme';
import { VyToastContainer } from './components/new-design/feedback/VyToastContainer';
import { SandboxDialog } from './components/utility/SandboxDialog';
import { useGlobalVariable } from './hooks/use-global-variable';
import { useIntegrations } from './hooks/use-integrations';
import { BillingCyclePage } from './pages/billing-cycle/BillingCyclePage';
import { ContractPage } from './pages/contract/ContractPage/ContractPage';
import { ContractRevisePage } from './pages/contract/ContractRevisePage/ContractRevisePage';
import { CustomerContractPage } from './pages/contract/CustomerContractPage/CustomerContractPage';
import { CustomerContractsPage } from './pages/contract/CustomerContractsPage/CustomerContractsPage';
import { UpdateContractPage } from './pages/contract/UpdateContractPage/UpdateContractPage';
import { CreateCreditNotePage } from './pages/credit-notes/CreateCreditNotePage/CreateCreditNotePage';
import { CustomerCreditNotesPage } from './pages/customers/CustomerCreditNotesPage/CustomerCreditNotesPage';
import { CreateRefundPage } from './pages/refund/CreateRefundPage';
import { CustomerPortalPage } from './pages/customer-portal/CustomerPortalPage/CustomerPortalPage';
import { CreateCustomerPage } from './pages/customers/CreateCustomerPage/CreateCustomerPage';
import { CustomerInvoicesPage } from './pages/customers/CustomerInvoicesPage/CustomerInvoicesPage';
import { CustomerLayout } from './pages/customers/CustomersPage/customers-page-components/CustomerLayout/CustomerLayout';
import { CustomerOverview } from './pages/customers/CustomersPage/customers-page-components/CustomerOverview/CustomerOverview';
import { CustomersPage } from './pages/customers/CustomersPage/CustomersPage';
import { UpdateCustomerSettings } from './pages/customers/UpdateCustomerSettingsPage/UpdateCustomerSettings';
import { DataMeteringAdminPage } from './pages/data-metering/AdminPage';
import { DataMeteringPage } from './pages/data-metering/DataMeteringPage/DataMeteringPage';
import { EmptyPage } from './pages/empty/EmptyPage';
import { EventsPage } from './pages/events/EventsPage';
import { AiAgentsPage } from './pages/ai-agents/AiAgentsPage';
import { Home } from './pages/home-v2/Home';
import { IntegrationProviderPage } from './pages/integrations/IntegrationProviderPage/IntegrationProviderPage';
import { IntegrationsPage } from './pages/integrations/IntegrationsPage/IntegrationsPage';
import { ApprovalFlowPage } from './pages/invoices/ApprovalFlowPage/ApprovalFlowPage';
import { INVOICE_CREATION_STEPS } from './pages/invoices/CreateInvoicePage/create-invoice-page-contexts/create-invoice-context/create-invoice-context-types/invoice-creation-step';
import { CreateInvoicePage } from './pages/invoices/CreateInvoicePage/CreateInvoicePage';
import { CustomerInvoicePage } from './pages/invoices/CustomerInvoicePage/CustomerInvoicePage';
import { DynamicInvoiceViewPage } from './pages/invoices/DynamicInvoiceViewPage';
import { InvoicesPage } from './pages/invoices/InvoicesPage';
import { LightInvoiceViewPage } from './pages/invoices/LightInvoiceViewPage';
import { PaymentsPage } from './pages/payments/PaymentsPage/PaymentsPage';
import { CreatePlanPage } from './pages/plans/CreatePlanPage/CreatePlanPage';
import { PlansPage } from './pages/plans/PlansPage';
import { PlanViewPage } from './pages/plans/PlanViewPage/PlanViewPage';
import { UpdatePlanPage } from './pages/plans/UpdatePlanPage/UpdatePlanPage';
import { CreateProductPage } from './pages/products/CreateProductPage/CreateProductPage';
import { ProductsPage } from './pages/products/ProductsPage/ProductsPage';
import { AnonymousCustomersReportPage } from './pages/reports/anonymous-customers-report-page/AnonymousCustomersReportPage';
import { CommitmentUsageReportPage } from './pages/reports/commitment-usage-report-page/CommitmentUsageReportPage';
import { BillingForecastReportPage } from './pages/reports/billing-forecast-report-page/BillingForecastReportPage';
import { CustomerRevenueReportPage } from './pages/reports/customer-revenue-report-page/CustomerRevenueReportPage';
import { JournalEntriesReportPage } from './pages/reports/journal-entries-report-page/JournalEntriesReportPage';
import { UsageReportV2ReportPage } from './pages/reports/usage-report-v2-report-page/UsageReportV2ReportPage';
import { CommitmentVsUsageReportPage } from './pages/reports/commitment-vs-usage-report-page/CommitmentVsUsageReportPage';
import { UnmeteredEventsReportPage } from './pages/reports/unmetered-events-report-page/UnmeteredEventsReportPage';
import { RecurringRevenueReportPage } from './pages/reports/recurring-revenue-report-page/RecurringRevenueReportPage';
import { NrrGrrReportPage } from './pages/reports/nrr-grr-report-page/NrrGrrReportPage';
import { CustomerMasterReportPage } from './pages/reports/customer-master-report-page/CustomerMasterReportPage';
import { FailedInvoiceDeliveriesReportPage } from './pages/reports/failed-invoice-deliveries-report-page/FailedInvoiceDeliveriesReportPage';
import { MonthlyClosureReportPage } from './pages/reports/monthly-closure-report-page/MonthlyclosureReportPage';
import { ProductUsageReportPage } from './pages/reports/product-usage-report-page/ProductUsageReportPage';
import { ReportsPage } from './pages/reports/reports-page/ReportsPage';
import { UnchargedUsageReportPage } from './pages/reports/uncharged-usage-report-page/UnchargedUsageReportPage';
import { SettingsPage } from './pages/settings/SettingsPage/SettingsPage';
import { LoginPage } from './pages/users/LoginPage/LoginPage';
import { RegisterConfirmationPage } from './pages/users/RegisterConfirmationPage/RegisterConfirmationPage';
import { RegisterPage } from './pages/users/RegisterPage/RegisterPage';
import { ResetPasswordPage } from './pages/users/ResetPasswordPage/ResetPasswordPage';
import { McpConsentPage } from './pages/mcp-consent/McpConsentPage';
import PermissionsProtectedRoute from './router/PermissionsProtectedRoute';
import { ProtectedRoute } from './router/ProtectedRoute';
import RootLayout from './router/RootLayout';
import { isNotProduction, isProduction } from './utils/env-indicator';
import { HotjarTracker } from './utils/hotjar-tracker.tsx';
import { AiCopilotAppShell } from './components/ai-copilot/AiCopilotAppShell';

export function App() {
  const [showSandboxDialog] = useGlobalVariable('showSandboxDialog');

  const UsageAdminPage = isNotProduction() ? <DataMeteringAdminPage /> : <EmptyPage />;
  useIntegrations();

  const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

  return (
    <Router>
      {isProduction() && <HotjarTracker />}
      {showSandboxDialog && createPortal(<SandboxDialog />, document.body)}
      <SentryRoutes>
        <Route
          path="login"
          element={
            <ThemeProvider theme={oldVyTheme}>
              <LoginPage />
            </ThemeProvider>
          }
        />

        <Route
          path="register"
          element={
            <ThemeProvider theme={oldVyTheme}>
              <RegisterPage />
            </ThemeProvider>
          }
        />

        <Route
          path="confirm"
          element={
            <ThemeProvider theme={oldVyTheme}>
              <RegisterConfirmationPage />
            </ThemeProvider>
          }
        />
        <Route
          path="password/reset"
          element={
            <ThemeProvider theme={oldVyTheme}>
              <ResetPasswordPage />
            </ThemeProvider>
          }
        />

        <Route path="mcp-consent" element={<McpConsentPage />} />

        <Route
          path="invoices/shared"
          element={
            <ThemeProvider theme={oldVyTheme}>
              <CustomerPortalPage />
            </ThemeProvider>
          }
        />

        <Route
          element={
            <ProtectedRoute>
              <ThemeProvider theme={oldVyTheme}>
                <Outlet />
              </ThemeProvider>
            </ProtectedRoute>
          }
        >
          <Route element={<AiCopilotAppShell />}>
          <Route path="" element={<Navigate to="home" replace />} />

          <Route
            path="home"
            element={
              <ThemeProvider theme={vyTheme}>
                <Outlet />
              </ThemeProvider>
            }
          >
            <Route path="overview" element={<Home />} />
            <Route path="" element={<Navigate to="overview" replace />} />
          </Route>

          {/*
            New design-system page. AiAgentsPage internally wraps in
            VyThemeProvider, so we deliberately do NOT wrap it in the
            legacy `vyTheme`/`oldVyTheme` ThemeProvider here — the page
            owns its theme and must not inherit the legacy one.
          */}
          <Route path="ai-agents" element={<AiAgentsPage />} />

          <Route
            path="integrations"
            element={<PermissionsProtectedRoute requiredPermissions={[PERMISSIONS.INTEGRATION_READ]} />}
          >
            <Route path="" element={<IntegrationsPage />} />

            <Route path=":provider" element={<IntegrationProviderPage />} />
          </Route>

          <Route
            path="plans"
            element={<PermissionsProtectedRoute requiredPermissions={[PERMISSIONS.PLAN_READ]} />}
          >
            <Route path="" element={<PlansPage />} />
            <Route path="get-started" element={<Navigate to=".." replace />} />

            <Route path=":planId">
              <Route path="" element={<PlanViewPage />} />

              <Route path="update" element={<UpdatePlanPage />} />
            </Route>

            <Route path="create" element={<CreatePlanPage />} />

            <Route
              path="contracts"
              element={<PermissionsProtectedRoute requiredPermissions={[PERMISSIONS.PLAN_READ]} />}
            >
              <Route path=":contractId" element={<ContractPage />} />
              <Route path=":contractId/update" element={<UpdateContractPage />} />
              <Route path=":contractId/revise" element={<ContractRevisePage />} />
            </Route>
          </Route>
          <Route
            path="invoices"
            element={<PermissionsProtectedRoute requiredPermissions={[PERMISSIONS.INVOICE_READ]} />}
          >
            <Route path="" element={<Navigate to="overview" replace />} />
            <Route
              path=":invoiceId/customer/:customerId"
              element={
                <CustomerInvoicePage>
                  <Outlet />
                </CustomerInvoicePage>
              }
            >
              <Route path="" element={<Navigate to="dynamic" replace />} />

              <Route
                path="dynamic"
                element={<DynamicInvoiceViewPage idType={DYNAMIC_INVOICE_ID_TYPES.Invoice} />}
              />

              <Route path="preview" element={<LightInvoiceViewPage />} />
            </Route>
            <Route path="overview">
              <Route path="" element={<InvoicesPage />} />

              <Route
                path="dynamic/:invoiceId"
                element={<DynamicInvoiceViewPage idType={DYNAMIC_INVOICE_ID_TYPES.Invoice} />}
              />

              <Route path="light/:invoiceId" element={<LightInvoiceViewPage />} />
            </Route>
            <Route path="monthly-closure" element={<BillingCyclePage />} />
            <Route
              path="payments"
              element={<PermissionsProtectedRoute requiredPermissions={[PERMISSIONS.INVOICE_READ]} />}
            >
              <Route path="" element={<PaymentsPage />} />
            </Route>
            <Route
              path="create"
              element={<PermissionsProtectedRoute requiredPermissions={[PERMISSIONS.INVOICE_CREATE]} />}
            >
              <Route
                path=""
                element={<CreateInvoicePage defaultInvoiceCreationStep={INVOICE_CREATION_STEPS.settings} />}
              />
            </Route>

            <Route path="monthly-approval" element={<ApprovalFlowPage />} />
          </Route>

          <Route
            path="credit-notes"
            element={<PermissionsProtectedRoute requiredPermissions={[PERMISSIONS.CREDIT_LEDGER_CREATE]} />}
          >
            <Route path="create" element={<CreateCreditNotePage />} />
          </Route>

          <Route
            path="refund"
            element={<PermissionsProtectedRoute requiredPermissions={[PERMISSIONS.INVOICE_CREATE]} />}
          >
            <Route path="create" element={<CreateRefundPage />} />
          </Route>

          <Route
            path="/products"
            element={<PermissionsProtectedRoute requiredPermissions={[PERMISSIONS.PRODUCT_READ]} />}
          >
            <Route path="" element={<ProductsPage />} />

            <Route path="create" element={<CreateProductPage />} />

            <Route path=":productId/edit" element={<CreateProductPage />} />
          </Route>

          <Route
            path="customers"
            element={
              <ThemeProvider theme={vyTheme}>
                <PermissionsProtectedRoute requiredPermissions={[PERMISSIONS.CUSTOMER_READ]} />
              </ThemeProvider>
            }
          >
            <Route
              path=":customerId"
              element={
                <CustomerLayout>
                  <Outlet />
                </CustomerLayout>
              }
            >
              <Route path="overview" element={<CustomerOverview />} />
              <Route path="settings" element={<UpdateCustomerSettings />} />
              <Route path="invoices" element={<CustomerInvoicesPage />} />
              <Route path="credit-notes" element={<CustomerCreditNotesPage />} />

              <Route path="contracts">
                <Route path="" element={<CustomerContractsPage />} />

                <Route path=":contractId" element={<CustomerContractPage />} />
              </Route>
            </Route>
          </Route>

          <Route
            path="customers"
            element={
              <ThemeProvider theme={vyTheme}>
                <PermissionsProtectedRoute requiredPermissions={[PERMISSIONS.CUSTOMER_READ]} />
              </ThemeProvider>
            }
          >
            <Route path="" element={<CustomersPage />} />
            <Route path="create" element={<CreateCustomerPage />} />

            <Route
              path=":customerId/invoices/:invoiceId"
              element={
                <CustomerInvoicePage>
                  <Outlet />
                </CustomerInvoicePage>
              }
            >
              <Route path="" element={<Navigate to="dynamic" replace />} />

              <Route
                path="dynamic"
                element={<DynamicInvoiceViewPage idType={DYNAMIC_INVOICE_ID_TYPES.Invoice} />}
              />

              <Route path="preview" element={<LightInvoiceViewPage />} />
            </Route>
          </Route>

          <Route
            path="usage"
            element={
              <PermissionsProtectedRoute
                requiredPermissions={[
                  PERMISSIONS.METER_READ,
                  PERMISSIONS.MEASUREMENT_READ,
                  PERMISSIONS.EVENT_READ,
                ]}
              />
            }
          >
            <Route
              path="metering"
              element={
                <PermissionsProtectedRoute
                  requiredPermissions={[PERMISSIONS.METER_READ, PERMISSIONS.MEASUREMENT_READ]}
                />
              }
            >
              <Route path="" element={<DataMeteringPage />} />
            </Route>
            <Route
              path="events"
              element={<PermissionsProtectedRoute requiredPermissions={[PERMISSIONS.EVENT_READ]} />}
            >
              <Route path="" element={<EventsPage />} />
            </Route>
          </Route>

          <Route
            path="reports"
            element={<PermissionsProtectedRoute requiredPermissions={[PERMISSIONS.REPORT_READ]} />}
          >
            <Route path="" element={<ReportsPage />} />
            <Route path="commitment-usage" element={<CommitmentUsageReportPage />} />
            <Route path="commitment-usage/:reportId" element={<CommitmentUsageReportPage />} />
            <Route path="product/:reportId" element={<ProductUsageReportPage />} />
            <Route path="uncharged-usage/:reportId" element={<UnchargedUsageReportPage />} />
            <Route path="commitment-usage" element={<CommitmentUsageReportPage />} />
            <Route path="commitment-usage/:reportId" element={<CommitmentUsageReportPage />} />

            <Route path="monthly-closure" element={<MonthlyClosureReportPage />} />

            <Route path="monthly-closure/:reportId" element={<MonthlyClosureReportPage />} />

            <Route path="failed-invoice-deliveries" element={<FailedInvoiceDeliveriesReportPage />} />
            <Route
              path="failed-invoice-deliveries/:reportId"
              element={<FailedInvoiceDeliveriesReportPage />}
            />

            <Route path="product/:reportId" element={<ProductUsageReportPage />} />
            <Route path="uncharged-usage/:reportId" element={<UnchargedUsageReportPage />} />
            <Route path="recurring-revenue" element={<RecurringRevenueReportPage />} />

            <Route path="recurring-revenue/:reportId" element={<RecurringRevenueReportPage />} />

            <Route path="nrr-grr" element={<NrrGrrReportPage />} />

            <Route path="customer-revenue" element={<CustomerRevenueReportPage />} />

            <Route path="journal-entries" element={<JournalEntriesReportPage />} />

            <Route path="usage-report-v2" element={<UsageReportV2ReportPage />} />

            <Route path="billing-forecast" element={<BillingForecastReportPage />} />

            <Route path="commitment-vs-usage" element={<CommitmentVsUsageReportPage />} />

            <Route path="unmetered-events" element={<UnmeteredEventsReportPage />} />

            <Route path="anonymous-customers" element={<AnonymousCustomersReportPage />} />

            <Route path="anonymous-customers/:reportId" element={<AnonymousCustomersReportPage />} />

            <Route path="customer-master" element={<CustomerMasterReportPage />} />
          </Route>
          <Route
            path="settings"
            element={<PermissionsProtectedRoute requiredPermissions={[PERMISSIONS.USER_READ]} />}
          >
            <Route path="" element={<SettingsPage />} />
          </Route>
          </Route>
        </Route>

        <Route
          element={
            <ProtectedRoute>
              <RootLayout />
            </ProtectedRoute>
          }
        >
          <Route path="usage">
            <Route path="metering/admin" element={UsageAdminPage} />
          </Route>

          <Route path="*" element={<EmptyPage />} />
        </Route>
      </SentryRoutes>
      <VyToastContainer />
    </Router>
  );
}

export default App;
